import React, { useState } from 'react'
import { Box, Typography, Tabs, Tab } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'

import AccordionComponent from '../Accordion'
import AlertBanner from '../AlertBanner'
import CallToAction from '../CallToAction'
import CardComponent from '../Card'
import CodeModule from '../CodeModule'
import Form from '../Form'
import HelpPageList from '../List/Variants/HelpPageList'
import HelpImage from '../Image/Variants/HelpImage'
import Image from '../Image'
import ColumnsLayout from '../Layout/Columns/Columns'
import { BLOCKS, ContentfulToComponent, ContentfulToComponentProps, RenderNode } from '../RichText/types'
import TextComponent from '../Text'
import { TabItem, TabModuleProps } from './type'
import RichText from '../RichText'
import getIdFromString from '../../utils/getIdFromString'

const TabPanel: React.FC<{ children: React.ReactNode; value: number; index: number }> = ({
  children,
  value,
  index,
}) => {
  return (
    <Box
      sx={{
        transition: 'all ease-in-out 800ms',
        opacity: value !== index ? 0 : '',
        visibility: value !== index ? 'hidden' : 'visible',
      }}
    >
      {value === index && <Box>{children}</Box>}
    </Box>
  )
}

const TabModule = ({ tabs, inverted }: TabModuleProps) => {
  const { palette } = useTheme()
  const isMobile = useMediaQuery(useTheme().breakpoints.down('md'))

  const styles = {
    h2: {
      scrollMarginTop: '80px',
      marginBottom: '.5em',
      wordBreak: 'break-word',
      '&:only-child': {
        marginTop: -5,
      },
      '&:first-child': {
        marginTop: 0,
      },
    },

    container: {
      color: inverted ? palette.common.white : palette.common.black,
    },
  }
  const getTabRootStyles = (tab: TabItem) => ({
    color: inverted ? palette.grey[400] : palette.grey[500],
    '&:hover': {
      color: inverted ? palette.common.white : palette.common.black,
    },
    '&.MuiTab-root': {
      paddingLeft: tab.iconPosition === 'top' ? '14px' : '18px',
      paddingRight: tab.iconPosition === 'top' ? '14px' : '18px',
    },
    '&.Mui-selected': {
      color: inverted ? palette.common.white : palette.common.black,
    },
  })
  const getTabImageStyles = (tab: TabItem) => ({
    marginRight: tab.iconPosition === 'start' ? '8px' : '0',
    marginBottom: tab.iconPosition === 'top' ? '10px' : '0',
    height: tab.iconPosition === 'start' ? '25px' : 'auto',
    width: tab.iconPosition === 'start' ? '25px' : tab.iconPosition === 'top' && isMobile ? '130px' : '140px',
  })
  const getHashValue = () => {
    if (typeof window !== 'undefined') {
      return window.location.hash.replace('#', '')
    }
    return
  }

  //  To Get the hash value from the URL
  const activeTab = getHashValue() || ''

  const renderNode: Partial<RenderNode> = {
    [BLOCKS.HEADING_2]: (node, children) => {
      const element = (
        <Typography component="h2" variant={'h5'} sx={styles.h2}>
          {children}
        </Typography>
      )
      return element
    },
  }

  const propsMap: Partial<ContentfulToComponentProps> = {
    HelpUniverseComponentAccordion: <AccordionComponentProps,>(props: AccordionComponentProps) => ({
      ...(props as unknown as AccordionComponentProps),
    }),

    HelpUniverseComponentImage: <HelpImageProps,>(props: HelpImageProps) => ({
      ...(props as unknown as HelpImageProps),
    }),

    HelpUniverseComponentAlert: <IAlertBanner,>(props: IAlertBanner) => ({
      ...(props as unknown as IAlertBanner),
    }),

    HelpUniverseComponentList: <HelpPageListComponentProps,>(props: HelpPageListComponentProps) => ({
      ...(props as unknown as HelpPageListComponentProps),
    }),

    HelpUniverseComponentCta: <CallToActionType,>(props: CallToActionType) => ({
      ...(props as unknown as CallToActionType),
    }),

    HelpUniverseModuleDynamicComponents: <CodeModuleComponentProps,>(props: CodeModuleComponentProps) => ({
      ...(props as unknown as CodeModuleComponentProps),
    }),

    HelpUniverseModuleForm: <FormProps,>(props: FormProps) => ({
      ...(props as unknown as FormProps),
    }),

    HelpUniverseLayoutColumns: <ColumnsLayoutComponent,>(props: ColumnsLayoutComponent) => ({
      ...(props as unknown as ColumnsLayoutComponent),
    }),
    HelpUniverseComponentCard: <CardProps,>(props: CardProps) => ({
      ...(props as unknown as CardProps),
    }),
    HelpUniverseComponentText: <TextComponentProps,>(props: TextComponentProps) => ({
      ...(props as unknown as TextComponentProps),
    }),
  } as const

  const tabComponentMap: Partial<ContentfulToComponent> = {
    'HelpUniverseComponentAccordion': AccordionComponent,
    'HelpUniverseComponentImage': HelpImage,
    'HelpUniverseComponentAlert': AlertBanner,
    'HelpUniverseComponentList': HelpPageList,
    'HelpUniverseComponentCta': CallToAction,
    'HelpUniverseModuleDynamicComponents': CodeModule,
    'HelpUniverseModuleForm': Form,
    'HelpUniverseLayoutColumns': ColumnsLayout,
    'HelpUniverseComponentCard': CardComponent,
    'HelpUniverseComponentText': TextComponent,
  } as const

  const selectedTabIndex =
    tabs.findIndex(tab => getIdFromString(tab?.title) === activeTab) === -1
      ? 0
      : tabs.findIndex(tab => getIdFromString(tab?.title) === activeTab)

  const [value, setValue] = useState(selectedTabIndex)

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue)
  }
  return (
    <Box sx={styles.container}>
      <Tabs
        value={value}
        onChange={handleChange}
        aria-label="tab module"
        TabIndicatorProps={{
          sx: {
            backgroundColor: inverted ? palette.common.white : palette.primary.main,
          },
        }}
      >
        {tabs.map((tab, index) => (
          <Tab
            key={index}
            sx={getTabRootStyles(tab)}
            id={getIdFromString(tab.title)}
            wrapped={true}
            icon={tab.image ? <Image imageStyles={getTabImageStyles(tab)} {...tab.image} /> : ''}
            iconPosition={tab.iconPosition}
            label={tab.title}
          />
        ))}
      </Tabs>
      {tabs.map((tab, index) => (
        <TabPanel key={index} value={value} index={index}>
          <RichText
            raw={tab?.text?.raw}
            references={tab?.text?.references}
            renderNode={renderNode}
            componentMap={tabComponentMap as ContentfulToComponent}
            propsMap={propsMap as ContentfulToComponentProps}
          />
        </TabPanel>
      ))}
    </Box>
  )
}

export default TabModule
